import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Body2, Caption1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Tooltip } from '@ui/components/Tooltip'
import { AskIcon } from '@ui/icons/Ask.icon'

import { useWindowResize } from '@utils/domUtils'

import { SwapFreeLevelEnum } from '@model/SwapFreeLevelEnum'
import { TradingAccount } from '@model/TradingAccount'

import { Box } from '@shared/components/Box'

import { SwapFreeProgressBar } from './SwapFreeProgressBar'

import styles from './SwapFree.module.scss'

interface Props {
  tradingAccount: TradingAccount
  isFullWidth?: boolean
  swapFreeIsDisabled: boolean
  onContactSupport: () => void
  onOpenInfoModal: () => void
}

export const SwapFree: FC<Props> = ({
  tradingAccount,
  isFullWidth,
  swapFreeIsDisabled,
  onContactSupport,
  onOpenInfoModal,
}) => {
  const { t } = useTranslation()

  const isMobile = useWindowResize()

  const { ratingValue, swapFreeLevel, swapFreeOption } = tradingAccount

  const getLevelStatus = (level: number) => {
    switch (level) {
      case SwapFreeLevelEnum.Green:
        return t('Swap Free.Level.High')
      case SwapFreeLevelEnum.Orange:
        return t('Swap Free.Level.Medium')
      case SwapFreeLevelEnum.Red:
        return t('Swap Free.Level.Low')
      default:
        return ''
    }
  }
  const tooltipProps = swapFreeIsDisabled
    ? {
        heading: t('Swap Free.Swap Free Account'),
        description: t(
          'Swap Free.Your Swap-Free benefit has been paused due to the rating remaining',
          { daysTillSuspended: tradingAccount?.swapFreeOption?.daysTillSuspended }
        ),
        secondaryButton: { label: t('Contact Support'), onClick: onContactSupport },
      }
    : {
        description: t('Swap Free.Click to see more info'),
      }

  return (
    <Box className={styles.swapFree}>
      <Box flex justify='space-between' align='center'>
        <Box flex gap='x4'>
          {isMobile ? (
            <Body2 color='text-med-em'>{t('Swap Free.Swap Free')}</Body2>
          ) : (
            <Caption1>{t('Swap Free.Swap Free')}</Caption1>
          )}
          {!isMobile || swapFreeIsDisabled ? (
            <Tooltip {...tooltipProps}>
              <ActionIcon icon={AskIcon} size='x16' onClick={onOpenInfoModal} />
            </Tooltip>
          ) : (
            <ActionIcon icon={AskIcon} size='x16' onClick={onOpenInfoModal} />
          )}
        </Box>
        <Badge type='black' contrast='high' size='xs'>
          {getLevelStatus(swapFreeIsDisabled ? SwapFreeLevelEnum.Red : swapFreeLevel.id)}
        </Badge>
      </Box>
      <SwapFreeProgressBar
        ratingValue={ratingValue}
        red={swapFreeOption?.red ?? 0}
        green={swapFreeOption?.green ?? 0}
        disabled={swapFreeIsDisabled}
        isFullWidth={isFullWidth}
        expandedRange
      />
    </Box>
  )
}
